// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import {
  Observable,
  combineLatest,
  lastValueFrom,
  map,
  tap,
  filter,
  shareReplay,
  distinctUntilChanged,
  switchMap,
  take,
} from 'rxjs';
import {
  ComponentModel,
  DataSourceGroupModel,
  EntireOverviewModel,
  IntegratedContentAvailabilityModel,
  SystemDetailsModel
} from 'src/app/api/remote-api/.portal.generated/models';
import { CoilService } from 'src/app/modules/coils/services/coil.service';
import { CoolingCompanionService } from 'src/app/modules/cooling-companion/services/cooling-companion.service';
import { PatternMatchComponent } from 'src/app/modules/patternmatch/components/pattern-match/pattern-match.component';
import { APICallGateComponent } from 'src/app/modules/shared/components/api-call-gate.component';
import { ScreenLayoutService } from 'src/app/modules/shared/services/screen-layout-service/screen-layout.service';
import { Observe } from 'src/app/modules/shared/utils/as-observable';
import { TubeService } from 'src/app/modules/tube/services/tube.service';
import { ExtendedComponentModel } from '../../../../_interfaces/component-model-extension';
import { PatternMatchDataService } from '../../../patternmatch/services/pattern-match-data/pattern-match-data.service';
import {
  ChangeInfo,
  DeepReadonly,
} from '../../../shared/utils/api-call-state.util';
import { UrlBuilder } from '../../../shared/utils/urlBuilder';
import { ApplicationInsightsService } from '../../services/application-insights/application-insights-service/application-insights.service';
import { LastdataSourceRefreshInformationsService } from '../../services/lastdataSourceRefreshInformations.service';
import { SystemOverviewService } from '../../services/systemOverview.service';
import { LastDataSourceRefreshComponent } from '../lastDataSourceRefresh/lastDataSourceRefresh.component';
import { RefreshDataSourcesButtonComponent } from '../refresh-data-sources-button/refresh-data-sources-button.component';
import { ComponentGroup } from '../../interfaces/ComponentGroup';
import { CoolingSystemComponentService } from '../../services/cooling-system-component.service';
import { ProcessedOverview } from '../../interfaces/ProcessedOverview';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

// break points
const maxWidthOneColumn = 300;
const maxWidthTwoColumns = 700;

export enum SeverityStatus {
  error = 'error',
  warning = 'warning',
  success = 'success',
}

@Component({
  selector: 'app-component-overview',
  templateUrl: './overview.component.html',
  styleUrls: ['./overview.component.scss'],
  standalone: true,

  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    TranslateModule,
    RefreshDataSourcesButtonComponent,
    LastDataSourceRefreshComponent,
    APICallGateComponent,
    PatternMatchComponent,
    PatternMatchComponent,
    CommonModule
  ],
})
export class OverviewComponent implements AfterViewInit, OnChanges {
  private host = inject(ElementRef);
  public systemOverviewService = inject(SystemOverviewService);
  protected tubeService = inject(TubeService);
  protected coilService = inject(CoilService);
  protected lastDataSourceRefreshService = inject(
    LastdataSourceRefreshInformationsService,
  );
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);
  private coolingSystemComponentService = inject(CoolingSystemComponentService);
  public screenLayoutService = inject(ScreenLayoutService);
  protected applicationInsights = inject(ApplicationInsightsService);
  patternMatchDataService = inject(PatternMatchDataService);
  coolingCompanionService = inject(CoolingCompanionService);
  private sanitizer = inject(DomSanitizer);

  @Input({ required: true }) systemDetails!: SystemDetailsModel;
  @ViewChild('iframeWrapper') iframeWrapper!: ElementRef;

  @Observe('systemDetails') systemDetails$!: Observable<
    typeof this.systemDetails
  >;

  protected popoverState: 'progress' | 'success' | 'failure' = 'progress';
  protected popoverLabel = '';
  protected popoverType: 'default' | 'confirmation' | 'error' = 'default';
  protected showSystemReportPopover = false;
  protected requestInProgress = false;
  protected autoCloseTimer: any = null;
  protected showFinalPopover = false;
  protected popoverTarget = '';
  protected progressHeader = '';
  protected popoverMessage = '';
  // tests need access
  changedComponents = new Set<number>();

  protected selectedTab = 'overview';
  protected safeAdditionalDataUrl!: SafeResourceUrl;
  protected isIntegratedContentAvailable = false;
  protected integratedContentUrl = '';
  private loadedZoomLevel = 1;
  protected zoomLevel = 1;
  protected isFullscreenActive = false;

  ngOnChanges(): void {
    this.systemOverviewService.entireOverview.request(this.systemDetails);
    this.checkIfIntegratedContentAvailable();
  }

  ngAfterViewInit(): void {
    this.onResize();
  }

  checkIfIntegratedContentAvailable(): void {
    combineLatest([
      this.systemOverviewService.currentModalityName$,
      this.systemDetails$
    ]).pipe(
      filter(([modalityName, systemDetails]) => !!modalityName && !!systemDetails),
      take(1),
      switchMap(([modalityName, systemDetails]) =>
        this.systemOverviewService.integratedContentAvailability.request({
          systemSerialNumber: systemDetails.systemSerialNumber,
          systemMaterialNumber: systemDetails.systemMaterialNumber,
          modalityName: modalityName!,
        }),
      ),
      takeUntilDestroyed(this.destroyRef),
    )
      .subscribe((response) => {
        const typedResponse = response as IntegratedContentAvailabilityModel | undefined;
        if (typedResponse) {
          this.isIntegratedContentAvailable = typedResponse.isContentAvailable ?? false;
          this.integratedContentUrl = typedResponse.url ?? '';
          
          //Sanitize the URL from the response
          if (this.integratedContentUrl) {
            this.safeAdditionalDataUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
              this.integratedContentUrl
            );
          }
        } else {
          this.isIntegratedContentAvailable = false;
          this.integratedContentUrl = '';
          this.safeAdditionalDataUrl =
            this.sanitizer.bypassSecurityTrustResourceUrl('about:blank');
        }
      });
  }

  // data sources
  entireOverview$ =
    this.systemOverviewService.entireOverview.lastSuccessfulData$;

  modalities$ = this.systemOverviewService.modalities.lastSuccessfulData$;

  // if any of these has not been called before they will
  // just silently do nothing
  // => in case we are on a coil page, tube refresh is a no-op
  refreshableApiCalls = [
    this.systemOverviewService.entireOverview,
    this.tubeService.dataSourceUpdateStatus,
    this.coilService.getCoilStatus,
    this.lastDataSourceRefreshService.lastDataSourceRefreshInformations,
    this.systemOverviewService.getSrsDataCollectionStatus,
  ];

  // derived data streams
  // modalityName needs systemDetails + modalities
  modalityName$ = combineLatest({
    modalities: this.modalities$,
    systemDetails: this.systemDetails$,
  }).pipe(
    map(function deriveModalityName({ systemDetails, modalities }) {
      // get name of modality, for whatever reason
      // the NAME (not id) needs to end up in a url later on..
      const modalityName =
        modalities.find((m) => m.id === systemDetails.modalityId)
          ?.displayName || '';
      return modalityName;
    }),
  );

  // computes grouped components
  processedComponentGroups$ = combineLatest({
    modalityName: this.modalityName$.pipe(filter((m) => !!m)),
    overview: this.entireOverview$.pipe(filter((o) => !!o)),
    systemDetails: this.systemDetails$.pipe(filter((s) => !!s)),
  }).pipe(
    // removed take(1) so updates propagate
    distinctUntilChanged(
      (prev, curr) =>
        prev.modalityName === curr.modalityName &&
        prev.overview === curr.overview &&
        prev.systemDetails === curr.systemDetails,
    ),
    map((args) => this.processOverview(args)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  // `flatGroupComponent$` is an observable that flattens the grouped components
  // from processedComponentGroups$ into a single array of components
  flatGroupComponent$ = this.processedComponentGroups$.pipe(
    map(
      (groups) =>
        // Use `flatMap` to extract the `components` array from each group and combine them into a single array.
        groups?.flatMap((group) => group.components) || [], // If `groups` is null or undefined, return an empty array.
    ),
  );

  hasNonUnnamedComponents$ = this.flatGroupComponent$.pipe(
    map((components) =>
      components.some(
        (component) => component.displayName.toLowerCase() !== 'unnamed',
      ),
    ),
  );

  // transient component state
  tooltipText = '';
  tooltipTarget = '';
  tooltipVisible = true;
  isComponentExpanded = true;

  onShowDetailsClick(filterName: string | null): void {
    if (filterName) {
      this.router.navigate(['/details'], {
        queryParams: { filter: filterName },
      });
    } else {
      console.warn('Filter name is not defined.');
    }
  }

  toggleExpandCollapse(): void {
    this.isComponentExpanded = !this.isComponentExpanded;
  }

  indicateRefresh(d: DeepReadonly<ChangeInfo[]>): void {
    // look for changed components
    {
      const entireOverview = d.find((item) =>
        /\/entireoverview\//.exec(item.url),
      ) as ChangeInfo<EntireOverviewModel>;
      if (entireOverview && !entireOverview.same) {
        // find changed components
        for (const comp of entireOverview.new.components) {
          const prev = entireOverview.old.components.find(
            (c) => c.id === comp.id,
          );
          if (prev && JSON.stringify(prev) !== JSON.stringify(comp)) {
            this.changedComponents.add(comp.id!);
          }
        }
      }
    }
  }

  mouseEnterComponent(component: ExtendedComponentModel): void {
    if (this.screenLayoutService.touchDevice$.getValue()) {
      return;
    }
    this.tooltipText = component.tooltip || '';
    this.tooltipVisible = true;
    this.tooltipTarget = `componentId_${component.id}`;
  }

  mouseLeaveComponent(): void {
    this.tooltipVisible = false;
  }

  sortComponentsBySeverity(
    components: Array<ExtendedComponentModel>,
  ): ExtendedComponentModel[] {
    components = components
      .filter((item) => item.stateIcon === 'error')
      .concat(components.filter((item) => item.stateIcon === 'warning'))
      .concat(
        components.filter(
          (item) => item.stateIcon !== 'error' && item.stateIcon !== 'warning',
        ),
      );
    if (components[0].stateIcon !== SeverityStatus.success) {
      components = components.splice(0, 6);
    }
    return components;
  }

  @HostListener('window:resize', ['$event'])
  onResize(): void {
    const h = this.host.nativeElement as HTMLElement;
    const w = h.clientWidth;

    if (w < maxWidthOneColumn) {
      h.setAttribute('cols', '1');
    } else if (w < maxWidthTwoColumns) {
      h.setAttribute('cols', '2');
    } else {
      h.setAttribute('cols', '3');
    }
  }

  groupComponents(
    component: ExtendedComponentModel,
    groups: ComponentGroup,
    systemDetails: DeepReadonly<SystemDetailsModel>,
  ): void {
    if (
      this.coolingSystemComponentService.checkIfCoolingSystemComponentIsOfCtNaeotomAlpha(
        component.id,
        systemDetails,
      )
    ) {
      // special case: CT NAEOTOM Alpha cooling system component
      component.stateIcon =
        component.stateIcon === SeverityStatus.success
          ? "url('/portal/assets/success-gray.svg')"
          : component.stateIcon;

      this.coolingCompanionService.updateCoolingSystemDisplayName(
        component,
        systemDetails.systemSerialNumber,
        systemDetails.systemMaterialNumber,
      );

      groups.forReview.components.push(component);
    } else {
      switch (component.stateIcon) {
        case SeverityStatus.error:
        case SeverityStatus.warning:
          groups.forReview.components.push(component);
          break;
        case SeverityStatus.success:
          groups.noActionRequired.components.push(component);
          break;
        default:
          groups.noData.components.push(component);
          break;
      }
    }
  }

  createEmptyGroups(): ComponentGroup {
    return {
      forReview: {
        className: 'for-review',
        displayName: 'Review Suggested',
        components: [] as ExtendedComponentModel[],
        topGroup: false,
      },
      noActionRequired: {
        className: 'no-action-required',
        displayName: 'No Action Required',
        components: [] as ExtendedComponentModel[],
        topGroup: false,
      },
      noData: {
        className: 'no-data',
        displayName: 'No Data Available',
        components: [] as ExtendedComponentModel[],
        topGroup: false,
        icon: 'no-findings',
      },
    };
  }

  // group components
  computeGroups(
    list: DeepReadonly<ExtendedComponentModel[]>,
    systemDetails: DeepReadonly<SystemDetailsModel>,
  ): ProcessedOverview[] {
    const groups = this.createEmptyGroups();

    list.forEach((component) => {
      this.groupComponents(component, groups, systemDetails);
    });

    // create icons for tube and coil companion
    // we know they are green, change icon to filled icon for
    // tube and coil, dashed otherwise
    groups.noActionRequired.components = groups.noActionRequired.components.map(
      (component) => ({
        ...component,
        stateIcon: (() => {
          if (
            component.id === 1 ||
            component.id === 15 ||
            component.id === 35
          ) {
            // coil and tube
            return "url('/portal/assets/success.svg')";
          }
          return "url('/portal/assets/success-gray.svg')";
        })(),
      }),
    );

    const groupsArray = [
      groups.forReview,
      groups.noActionRequired,
      groups.noData,
    ];
    // set first non-empty group to topGroup (so it shows on top by default)
    for (const group of groupsArray) {
      if (group.components.length > 0) {
        group.topGroup = true;
        break;
      }
    }
    return groupsArray;
  }

  // pure
  private static replaceComponentUrlsPlaceholder(
    component: DeepReadonly<ExtendedComponentModel>,
    dataSourceGroups: DeepReadonly<DataSourceGroupModel[]>,
    modalityName: string,
    systemDetails: DeepReadonly<SystemDetailsModel>,
  ): DeepReadonly<ExtendedComponentModel> {
    const dataSourceGroup = dataSourceGroups.find(
      (item) => item.id === component.dataSourceGroupId,
    );

    let compWithPatchedWebAppEndpoint = component;

    if (dataSourceGroup) {
      const url = UrlBuilder.replaceUrlPlaceholder(
        dataSourceGroup.webAppEndpoint || '',
        systemDetails,
        '',
        component.filterName || '',
        '',
        modalityName,
      );

      if (url?.startsWith('http')) {
        compWithPatchedWebAppEndpoint = {
          ...structuredClone(component),
          webAppEndpoint: url,
        };
      } else {
        compWithPatchedWebAppEndpoint = {
          ...structuredClone(component),
          webAppEndpoint: `${window.location.href}/component/${component.id}`,
        };
      }
    }
    return compWithPatchedWebAppEndpoint;
  }

  goToComponent(component: ComponentModel): void {
    this.patternMatchDataService.goToComponent(component, this.route);
  }

  processOverview({
    modalityName,
    overview,
    systemDetails,
  }: {
    modalityName: string;
    overview: DeepReadonly<EntireOverviewModel>;
    systemDetails: DeepReadonly<SystemDetailsModel>;
  }): {
    className: string;
    displayName: string;
    components: ExtendedComponentModel[];
    topGroup: boolean;
    icon?: string;
  }[] {
    // filter out hidden components
    let visibleComponents = overview.components.filter(
      (component) => !component.isHidden,
    );

    // replace urls
    visibleComponents = visibleComponents.map((component) =>
      OverviewComponent.replaceComponentUrlsPlaceholder(
        structuredClone(component),
        overview.dataSourceGroups,
        modalityName,
        systemDetails,
      ),
    );

    const componentGroups = this.computeGroups(
      visibleComponents,
      systemDetails,
    );

    return componentGroups;
  }

  async copySystemReportToClipboard(): Promise<void> {
    this.applicationInsights.trackComponentUsageEvent('CopySystemReportToClipboardClicked',
      {
        systemSnapShot: 'CopyToClipboard',
      },
    );
    this.popoverTarget = 'clipboard-icon';
    this.popoverState = 'progress';
    this.progressHeader = 'label.copyToClipboardProgressHeader';
    this.popoverLabel = 'label.copyToClipboard';
    this.popoverType = 'default';
    this.showSystemReportPopover = true;

    const blob = new ClipboardItem({
      'text/plain': (async () => {
        try {
          const response = await lastValueFrom(
            this.systemOverviewService.systemInfoReport.request(
              this.systemDetails,
            ),
          );
          if (!response) {
            throw new Error('Empty response received from the server');
          }
          const iconElement = document.getElementById('clipboard-icon');
          if (iconElement) {
            iconElement.focus();
          }

          setTimeout(() => {
            this.showSystemReportPopover = true;
            this.popoverState = 'success';
            this.popoverLabel = 'label.copyToClipboardSucceed';
            this.popoverMessage = 'message.copyToClipboardSucceed';
            this.popoverType = 'confirmation';
          }, 2000);

          this.autoCloseTimer = setTimeout(() => {
            this.closeSystemReportPopover();
          }, 4000);
          return new Blob([response], { type: 'text/plain' });
        } catch (err) {
          console.error('Could not copy text: ', err);

          setTimeout(() => {
            this.showSystemReportPopover = true;
            this.popoverState = 'failure';
            this.popoverLabel = 'label.copyToClipboardFailed';
            this.popoverMessage = 'message.copyToClipboardFailed';
            this.popoverType = 'error';
          }, 2000);

          this.autoCloseTimer = setTimeout(() => {
            this.closeSystemReportPopover();
          }, 4000);
          return '';
        }
      })(),
    });

    try {
      if (document.hasFocus()) {
        await navigator.clipboard.write([blob]);
      } else {
        throw new Error('Document is not focused');
      }
    } catch (err) {
      console.error('Could not write to clipboard: ', err);

      setTimeout(() => {
        this.showSystemReportPopover = true;
        this.popoverState = 'failure';
        this.popoverLabel = 'label.copyToClipboardFailed';
        this.popoverMessage = 'message.copyToClipboardFailed';
        this.popoverType = 'error';
      }, 2000);

      this.autoCloseTimer = setTimeout(() => {
        this.closeSystemReportPopover();
      }, 4000);
    }
  }

  async closeSystemReportPopover(): Promise<void> {
    this.showSystemReportPopover = false;
    this.popoverState = 'progress';
  }

  getPopoverBorderColor(): string {
    switch (this.popoverType) {
      case 'confirmation':
        return 'green';
      case 'error':
        return 'red';
      default:
        return 'transparent';
    }
  }              
  async sendSystemReportByEmail(): Promise<void> {
    this.applicationInsights.trackComponentUsageEvent('EmailSystemReportClicked',
      {
        systemSnapShot: 'Email',
      },
    );
    this.popoverTarget = 'envelope-icon';
    this.popoverState = 'progress';
    this.progressHeader = 'label.sendSystemInfoEmailProgressHeader';
    this.popoverLabel = 'label.sendSystemInfoEmail';
    this.popoverType = 'default';
    this.showSystemReportPopover = true;

    this.systemOverviewService.emailSystemInfoReport
      .request(this.systemDetails)
      .pipe(
        tap((response: any) => {
          if (response === undefined) {
            throw new Error('Empty response received from the server');
          }
        }),
      )
      .subscribe({
        next: () => {
          setTimeout(() => {
            this.popoverState = 'success';
            this.popoverLabel = 'label.sendSystemInfoEmailSucceed';
            this.popoverMessage = 'message.sendSystemInfoEmailSucceed';
            this.popoverType = 'confirmation';
          }, 2000);

          this.autoCloseTimer = setTimeout(() => {
            this.closeSystemReportPopover();
          }, 4000);
        },
        error: () => {
          setTimeout(() => {
            this.popoverState = 'failure';
            this.popoverLabel = 'label.sendSystemInfoEmailFailed';
            this.popoverMessage = 'message.sendSystemInfoEmailFailed';
            this.popoverType = 'error';
          }, 2000);

          this.autoCloseTimer = setTimeout(() => {
            this.closeSystemReportPopover();
          }, 4000);
        },
      });
  }

  activateTab(tabValue: string): void {
    this.selectedTab = tabValue;
  }

  zoomIn(): void {
    console.log('Zoom In');
    this.zoomLevel = Number((this.zoomLevel + 0.1).toFixed(2));
  }

  zoomOut(): void {
    if (this.zoomLevel > this.loadedZoomLevel) {
      this.zoomLevel = Math.max(
        this.loadedZoomLevel,
        Number((this.zoomLevel - 0.1).toFixed(2)),
      );
    }
  }

  @HostListener('document:fullscreenchange')
  onFullscreenChange(): void {
    this.isFullscreenActive =
      document.fullscreenElement === this.iframeWrapper?.nativeElement;
  }

  async toggleFullscreen(): Promise<void> {
    const elem = this.iframeWrapper?.nativeElement as HTMLElement;
    if (!elem) {
      return;
    }

    if (document.fullscreenElement === elem) {
      await document.exitFullscreen();
    } else {
      await elem.requestFullscreen();
    }

    this.isFullscreenActive =
      document.fullscreenElement === this.iframeWrapper?.nativeElement;
  }
}
